import { layoutRect } from "../../utils/displayDensity";
import { memo, useCallback, useRef, useEffect } from "react";
import { Flashlight } from "lucide-react";
import { DEFAULT_OPTION_HEIGHT } from "./virtualizedDropdownStyles";

const OptionRow = memo(
  ({
    optionProps,
    virtualRow,
    onPreview,
    previewType,
    measuredHeights,
    setMeasuredHeight,
    isMeasuring,
    showHighlightToggle,
    isHighlighted,
    onToggleHighlight,
  }) => {
    const isOption = optionProps.options.length > 0;
    const value = optionProps.data?.value;
    const debounceRef = useRef();
    const optionRef = useRef();
    const measuredRef = useRef(false);

    useEffect(() => {
      if (
        optionRef.current &&
        isOption &&
        isMeasuring &&
        !measuredRef.current
      ) {
        const height = layoutRect(optionRef.current).height;
        if (height !== measuredHeights[virtualRow.index]) {
          setMeasuredHeight(virtualRow.index, height);
          measuredRef.current = true;
        }
      }
    }, [
      virtualRow.index,
      isOption,
      measuredHeights,
      setMeasuredHeight,
      isMeasuring,
    ]);

    const triggerPreview = useCallback(
      (type, val, delay = 250) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onPreview?.(type, val);
        }, delay);
      },
      [onPreview]
    );

    const handleEnter = () => triggerPreview(previewType, value);
    const handleLeave = () => triggerPreview(null, null, 50);

    const showDualColumns = isOption && showHighlightToggle;
    const highlighted = showDualColumns ? !!isHighlighted?.(value) : false;

    const handleHighlightClick = (e) => {
      e.stopPropagation();
      e.preventDefault();
      onToggleHighlight?.(value);
    };

    const handleFilterClick = (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (optionProps.isSelected && !optionProps.isMulti) {
        optionProps.clearValue?.();
      } else {
        optionProps.selectOption?.(optionProps.data);
      }
    };

    return (
      <div
        ref={optionRef}
        role={optionProps.innerProps?.role}
        aria-selected={optionProps.isSelected}
        className={`${isOption && "rs-option"} ${
          optionProps.isSelected && "rs-selected"
        }`}
        style={{
          transform: `translateY(${virtualRow.start}px)`,
          position: isOption && "absolute",
          left: isOption && 0,
          top: 0,
          width: "100%",
          minHeight: DEFAULT_OPTION_HEIGHT,
          lineHeight: "1.4",
          padding: showDualColumns ? "5px 10px" : "5px 12px",
          cursor: isOption && "pointer",
          textAlign: !isOption && "center",
          fontSize: !isOption && "15px",
          color: !isOption ? "#999" : "#fff",
          whiteSpace: "normal",
          wordBreak: "break-word",
          display: showDualColumns ? "flex" : undefined,
          alignItems: "center",
          gap: showDualColumns ? 10 : undefined,
          boxSizing: "border-box",
        }}
        onClick={optionProps.innerProps?.onClick}
        onMouseDown={optionProps.innerProps?.onMouseDown}
        onMouseEnter={isOption ? handleEnter : undefined}
        onMouseLeave={isOption ? handleLeave : undefined}
        onFocus={isOption ? handleEnter : undefined}
        onBlur={isOption ? handleLeave : undefined}
      >
        {showDualColumns ? (
          <>
            <button
              type="button"
              onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onClick={handleHighlightClick}
              title={highlighted ? "Remove highlight" : "Highlight in chart"}
              aria-label={highlighted ? "Remove highlight" : "Highlight in chart"}
              aria-pressed={highlighted}
              style={{
                flex: "0 0 auto",
                width: 22,
                height: 22,
                borderRadius: 7,
                border: highlighted
                  ? "1px solid rgba(var(--ms-accent-rgb, 63,169,245),0.9)"
                  : "1px solid rgba(255,255,255,0.22)",
                background: highlighted
                  ? "rgba(var(--ms-accent-rgb, 63,169,245),0.22)"
                  : "rgba(255,255,255,0.05)",
                color: highlighted ? "var(--ms-accent, #3fa9f5)" : "rgba(255,255,255,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.12s ease",
                boxShadow: highlighted ? "0 0 8px rgba(var(--ms-accent-rgb, 63,169,245),0.45)" : "none",
              }}
            >
              <Flashlight size={14} aria-hidden="true" />
            </button>
            <span style={{ flex: 1, minWidth: 0 }}>{optionProps.children}</span>
            <button
              type="button"
              onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
              onClick={handleFilterClick}
              title={optionProps.isSelected ? "Remove filter" : "Apply filter"}
              aria-label={optionProps.isSelected ? "Remove filter" : "Apply filter"}
              aria-pressed={optionProps.isSelected}
              style={{
                flex: "0 0 auto",
                width: 18,
                height: 18,
                borderRadius: 5,
                border: optionProps.isSelected
                  ? "1px solid rgba(36,197,128,0.95)"
                  : "1px solid rgba(255,255,255,0.3)",
                background: optionProps.isSelected ? "rgba(36,197,128,0.2)" : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.12s ease",
                boxShadow: optionProps.isSelected ? "0 0 8px rgba(36,197,128,0.28)" : "none",
              }}
            >
              {optionProps.isSelected ? (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 12.5l5 5L20 6.5"
                    stroke="#7ae3b4"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : null}
            </button>
          </>
        ) : (
          optionProps.children
        )}
      </div>
    );
  }
);

export default OptionRow;
